import React, { useState } from 'react';
import { isSilenced } from '../../Alarms/utils/alarmUtils';
import type { Alarm } from '../types/Alarms.types';

import classNames from 'classnames';
import BaseEllipsisMenu from 'components/BaseEllipsisMenu/BaseEllipsisMenu';
import { BsCheck } from 'react-icons/bs';
import { FaRegEnvelope } from 'react-icons/fa';
import { GiSpeaker, GiSpeakerOff } from 'react-icons/gi';
import { toast } from 'react-toastify';

function AcknowledgeButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={
          {
            '--fill-color': 'var(--color-primary)',
            '--radius': '12px',
            color: 'var(--color-primary-foreground)',
          } as React.CSSProperties
        }
      >
        <BsCheck />
      </div>
      <p className="mb-0">Acknowledge</p>
    </div>
  );
}

function SilenceButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={
          {
            '--fill-color': 'var(--color-primary)',
            '--radius': '12px',
            color: 'var(--color-primary-foreground)',
          } as React.CSSProperties
        }
      >
        <GiSpeakerOff />
      </div>
      <p className="mb-0">Silence</p>
    </div>
  );
}

function UnsilenceButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={
          {
            '--fill-color': 'var(--color-primary)',
            '--radius': '12px',
            color: 'var(--color-primary-foreground)',
          } as React.CSSProperties
        }
      >
        <GiSpeaker />
      </div>
      <p className="mb-0">Unsilence</p>
    </div>
  );
}

function ShareViaEmailButton({ onClick }: { onClick: () => void }) {
  return (
    <div
      className="d-flex w-100 p-3 justify-content-start align-items-center gap-3"
      onClick={onClick}
    >
      <div
        className={classNames('circle-fill', 'center-content')}
        style={
          {
            '--fill-color': 'var(--color-primary)',
            '--radius': '12px',
            color: 'var(--color-primary-foreground)',
          } as React.CSSProperties
        }
      >
        <FaRegEnvelope />
      </div>
      <p className="mb-0">Share via Email</p>
    </div>
  );
}

interface AlarmEllipsesProps {
  alarm: Alarm;
  onAcknowledge?: (alarm: Alarm) => Promise<boolean>;
  onSilence?: (alarm: Alarm) => Promise<boolean>;
  onUnsilence?: (alarm: Alarm) => Promise<boolean>;
  onShare?: (alarm: Alarm) => void;
  enableAcknowledge?: boolean;
  enableSilence?: boolean;
  enableUnsilence?: boolean;
  enableShare?: boolean;
}

export const AlarmEllipses: React.FC<AlarmEllipsesProps> = ({
  alarm,
  onAcknowledge,
  onSilence,
  onUnsilence,
  onShare,
  enableAcknowledge = true,
  enableSilence = true,
  enableUnsilence = true,
  enableShare = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const silenced = isSilenced(alarm);

  const options: { component: React.ReactNode }[] = [
    enableAcknowledge && {
      component: (
        <AcknowledgeButton
          onClick={async () => {
            const success = await onAcknowledge?.(alarm);
            if (success) {
              toast.success(`Acknowledged ${alarm?.info?.type} alarm`);
            } else {
              toast.error(`Failed to acknowledge ${alarm?.info?.type} alarm`);
            }
          }}
        />
      ),
    },
    enableSilence &&
      !silenced && {
        component: (
          <SilenceButton
            onClick={async () => {
              const success = await onSilence?.(alarm);
              if (success) {
                toast.success(`Silenced ${alarm?.info?.type} alarm`);
              } else {
                toast.error(`Failed to silence ${alarm?.info?.type} alarm`);
              }
            }}
          />
        ),
      },
    enableUnsilence &&
      silenced && {
        component: (
          <UnsilenceButton
            onClick={async () => {
              const success = await onUnsilence?.(alarm);
              if (success) {
                toast.success(`Unsilenced ${alarm?.info?.type} alarm`);
              } else {
                toast.error(`Failed to unsilence ${alarm?.info?.type} alarm`);
              }
            }}
          />
        ),
      },
    enableShare &&
      onShare && {
        component: <ShareViaEmailButton onClick={() => onShare?.(alarm)} />,
      },
  ].filter(Boolean) as { component: React.ReactNode }[];

  return (
    <BaseEllipsisMenu
      options={options}
      open={menuOpen}
      onOpen={() => setMenuOpen(true)}
      onClose={() => setMenuOpen(false)}
    />
  );
};
